import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Add as AddIcon, Download as DownloadIcon, FileCopy as CloneIcon, Upload as UploadIcon, ArrowBackRounded as ArrowBackIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { colors } from '../../../theme/index.js';
import Bubble from '../../../components/Bubble.js';
import Button from '../../../components/Button.js';
import Text from '../../../components/Text.js';
import Tooltip from '../../../components/Tooltip.js';
import PlanDesignForm from './PlanDesignForm.js';
import Revisions from './Revisions.js';
import Modal from '../../../components/Modals/Modal.js';
import Loader from '../../../components/Loader.js';
import HeaderDiv from '../../../components/HeaderDiv.js';
import * as Services from '../../../services/index.js';
import DateInput from '../../../components/TextFields/DateInput.js';
import FileDropZoneModal from '../../../components/Modals/DropZoneModal.js';
import papaparse from 'papaparse';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
const ContentBlock = styled('div')({
  backgroundColor: colors.surface,
  boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
  display: 'block',
  margin: 'auto auto 30px auto',
  padding: '30px',
  position: 'relative',
  zIndex: 4
});
const HeaderTitleBlock = styled('div')({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center'
});
const AddIconStyled = styled(AddIcon)(({
  theme
}) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  padding: 0
}));
const DownloadIconStyled = styled(DownloadIcon)(({
  theme
}) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  padding: 0
}));
const CloneIconStyled = styled(CloneIcon)(({
  theme
}) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  padding: 0
}));
const UploadIconStyled = styled(UploadIcon)(({
  theme
}) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  padding: 0
}));
class PlanDesign extends PureComponent {
  state = {
    value: 0,
    designIDs: this.props.designs[this.props.selectedClassID] ? Object.keys(this.props.designs[this.props.selectedClassID]).sort((a, b) => this.props.designs[this.props.selectedClassID][a].title.replace('Design ', '') - this.props.designs[this.props.selectedClassID][b].title.replace('Design ', '')) // Order designIDs by design number
    : [],
    selectedDesignID: null,
    showingRevisions: false,
    unSavedChangesMade: false,
    showSaveMessageDialog: false,
    dialogCallback: null,
    effectiveDate: dates.getUTC(new Date(), 'YYYY/MM/DD'),
    showEffectiveDateDialog: false,
    anchorElUpload: null,
    anchorElDownload: null,
    errors: {
      lifeInsurance: {},
      deathDismemberment: {},
      dependentLife: {},
      std: {},
      ltd: {},
      criticalIllness: {},
      drugs: {},
      healthcare: {},
      outOfCountry: {},
      efap: {},
      virtualHealthcare: {},
      financialAwareness: {},
      dental: {},
      spendingAccounts: {}
    }
  };
  componentDidMount() {
    this.setState({
      selectedDesignID: this.state.designIDs[0]
    });
  }
  static getDerivedStateFromProps(props, state) {
    // Order designIDs by design number
    const designIDs = props.designs[props.selectedClassID] ? Object.keys(props.designs[props.selectedClassID]).sort((a, b) => props.designs[props.selectedClassID][a].title.replace('Design ', '') - props.designs[props.selectedClassID][b].title.replace('Design ', '')) : [];
    if (designIDs.length !== state.designIDs.length) {
      return {
        designIDs,
        value: designIDs.length - 1,
        selectedDesignID: designIDs[designIDs.length - 1]
      };
    }
    return null;
  }

  /**
   * On Unmount, clear the auto save and do a final save
   */
  async componentWillUnmount() {
    if (this.state.unSavedChangesMade && !this.props.overrideModularView) {
      const confirm = window.confirm(i18n.t(tokens.labels.planDesign.saveChangesConfirmation));
      if (confirm) {
        await this._saveDesign(false);
      }
    }
  }
  _downloadPlanDesignJson = () => {
    const classID = this.props.selectedClassID;
    const planDesign = this.props.designs[classID][this.state.selectedDesignID];
    const link = document.createElement('a');
    link.href = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(planDesign))}`;
    link.setAttribute('download', `${classID}_${this.state.selectedDesignID}.json`);
    link.setAttribute('target', "_blank");
    document.body.appendChild(link);
    link.click();
  };
  _downloadPlanDesignCsv = async () => {
    const classID = this.props.selectedClassID;
    const planDesign = this.props.designs[classID][this.state.selectedDesignID];
    const planDesignBenefitKeys = {
      'lifeInsurance': 'LIFE INSURANCE',
      'deathDismemberment': 'DEATH & DISMEMBERMENT',
      'dependentLife': 'DEPENDENT LIFE',
      'std': "STD",
      'ltd': "LTD",
      'criticalIllness': "CRITICAL ILLNESS",
      'drugs': "DRUGS",
      'healthcare': "HEALTHCARE",
      'outOfCountry': "OUT OF COUNTRY",
      'efap': "EFAP",
      'virtualHealthcare': "VIRTUAL HEALTHCARE",
      'financialAwareness': "FINANCIAL AWARENESS",
      'dental': "DENTAL",
      'spendingAccounts': "SPENDING ACCOUNTS"
    };
    let csv = '';
    const keys = Object.keys(planDesignBenefitKeys);
    for (const key of keys) {
      // Add the title as a separator line
      csv += `\n${planDesignBenefitKeys[key]}\n`;

      // eslint-disable-next-line no-await-in-loop
      const lines = await papaparse.unparse([planDesign[key]]);
      csv += `${lines}\n`;
    }
    const link = document.createElement('a');
    link.href = `data:text/csv;charset=utf-8,${csv}`;
    link.setAttribute('download', `${classID}_${this.state.selectedDesignID}.csv`);
    link.setAttribute('target', "_blank");
    document.body.appendChild(link);
    link.click();
  };
  _uploadPlanDesign = files => {
    const reader = new FileReader();
    reader.onload = e => {
      const planDesign = JSON.parse(e.target.result);
      this.props.createPlanDesignFromUpload(this.props.selectedClassID, planDesign);
    };
    reader.readAsText(files[0]);
    this.setState({
      uploadPlanDesignModalOpen: false
    });
  };

  /**
   * @param event
   * @param value
   * @private
   */
  _handleTabChange = (event, value) => {
    if (this.state.unSavedChangesMade) {
      this.setState({
        showSaveMessageDialog: true,
        dialogCallback: () => {
          this.setState({
            value,
            selectedDesignID: this.state.designIDs[value]
          });
        }
      });
      return;
    }
    this.setState({
      value,
      selectedDesignID: this.state.designIDs[value]
    });
  };
  _validateEffectiveDate = async () => {
    let effectiveDateError = await Services.validateInput('effectiveDate', this.state.effectiveDate);
    const startOfMonth = dates.getStartOfMonth();
    const effectiveDateUTC = dates.getUTC(this.state.effectiveDate);
    if (effectiveDateUTC < startOfMonth) {
      effectiveDateError = i18n.t(tokens.labels.planDesign.planDesignChangesBackdated);
    }
    await this.setState({
      effectiveDateError
    });
    if (effectiveDateError) {
      return false;
    }
    return true;
  };
  _saveDesign = async (revision = false) => {
    if (!this.state.selectedDesignID || this.props.overrideModularView) {
      return null;
    }
    if (this.props.currentAccountMeta.state >= 3 && !this.props.isMasterSite && !this.props.isPrivateLabel) {
      return null;
    }

    // if it's the master site, make sure we ask for an effective date
    if (this.props.isMasterSite) {
      const effectiveDateValid = await this._validateEffectiveDate();
      if (!effectiveDateValid) {
        return null;
      }
    }
    const success = await this.props.savePlanDesign(this.state.selectedDesignID, revision || this.props.designs[this.props.selectedClassID][this.state.selectedDesignID], this.props.isMasterSite || this.props.isPrivateLabel ? dates.getUTC(this.state.effectiveDate) : null);
    if (success) {
      this.setState({
        unSavedChangesMade: false,
        effectiveDate: dates.getUTC(new Date(), 'YYYY/MM/DD'),
        showEffectiveDateDialog: false
      });
      const content = document.getElementById("content");
      if (content) {
        document.getElementById("content").scroll(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
    }
    return null;
  };
  _toggleEffectiveDateDialog = () => {
    this.setState({
      showEffectiveDateDialog: !this.state.showEffectiveDateDialog
    });
  };
  _setChangesMadeFlag = () => {
    this.setState({
      unSavedChangesMade: true
    });
  };
  _createPlanDesign = () => {
    this.props.createPlanDesign(this.props.selectedClassID);
  };
  _clonePlanDesign = async () => {
    await this.props.clonePlanDesign(this.props.selectedClassID, this.state.selectedDesignID);
  };
  _toggleRevisions = () => {
    this.setState({
      showingRevisions: !this.state.showingRevisions
    });
  };
  setAnchorEl = (type, anchorEl) => {
    this.setState({
      [type]: anchorEl
    });
  };
  render() {
    const {
      designs,
      selectedClassID,
      selectedClass,
      isMasterSite,
      isPrivateLabel
    } = this.props;
    const whitelabel = this.props.whitelabel || {};
    if (this.state.showingRevisions) {
      return /*#__PURE__*/React.createElement(Revisions, {
        isMasterSite: isMasterSite,
        isQuote: this.props.isQuote,
        whitelabel: whitelabel,
        designs: designs,
        specialityProductMetaConfigs: this.props.specialityProductMetaConfigs,
        currentAccountMeta: this.props.currentAccountMeta,
        selectedClassID: selectedClassID,
        selectedDesignID: this.state.selectedDesignID,
        getPlanDesignRevisions: this.props.getPlanDesignRevisions,
        getSinglePlanDesignRevision: this.props.getSinglePlanDesignRevision,
        revertToRevision: this.props.revertToRevision,
        goBack: this._toggleRevisions,
        saveDesign: this._saveDesign,
        overrideModularView: this.props.overrideModularView,
        loading: this.props.loading
      });
    }
    const orderedDesigns = Object.keys(designs[this.props.selectedClassID]).sort((a, b) => designs[this.props.selectedClassID][a].title.replace('Design ', '') - designs[this.props.selectedClassID][b].title.replace('Design ', ''));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ContentBlock, null, /*#__PURE__*/React.createElement(HeaderDiv, null, /*#__PURE__*/React.createElement(HeaderTitleBlock, null, /*#__PURE__*/React.createElement(Button, {
      customStyle: {
        minWidth: "26px",
        width: "30px",
        marginRight: "5px",
        marginLeft: "-5px",
        padding: "2px"
      },
      onClick: this.props.goBack,
      transparent: true
    }, /*#__PURE__*/React.createElement(ArrowBackIcon, null)), /*#__PURE__*/React.createElement(Text, {
      large: true,
      bold: true,
      display: "block",
      margin: "20px 0"
    }, i18n.t(tokens.labels.planDesign.buildAQuote)), !this.props.isPrivateLabelAdvisor && (this.props.currentAccountMeta.state <= 3 || this.props.isRevision) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      transparent: true,
      color: "primary",
      "aria-controls": this.state.anchorElUpload ? 'demo-positioned-menu' : undefined,
      "aria-haspopup": "true",
      "aria-expanded": this.state.anchorElUpload ? 'true' : undefined,
      onClick: e => this.setAnchorEl('anchorElUpload', e.currentTarget),
      customStyle: {
        textTransform: "capitalize",
        fontSize: 14,
        color: whitelabel.primaryColor || colors.skyBlue
      }
    }, /*#__PURE__*/React.createElement(AddIconStyled, null), " ", i18n.t(tokens.labels.planDesign.addDesign)), /*#__PURE__*/React.createElement(Menu, {
      id: "demo-positioned-menu",
      "aria-labelledby": "demo-positioned-button",
      anchorEl: this.state.anchorElUpload,
      open: Boolean(this.state.anchorElUpload),
      onClose: () => this.setAnchorEl('anchorElUpload', null),
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      }
    }, /*#__PURE__*/React.createElement(MenuItem, {
      onClick: () => {
        this.setAnchorEl('anchorElUpload', null);
        this._createPlanDesign();
      }
    }, /*#__PURE__*/React.createElement(AddIconStyled, {
      style: {
        marginRight: "10px"
      }
    }), " ", i18n.t(tokens.labels.planDesign.newBlankDesign)), Object.keys(designs[selectedClassID]).length > 0 && /*#__PURE__*/React.createElement(MenuItem, {
      onClick: () => {
        this.setAnchorEl('anchorElUpload', null);
        this._clonePlanDesign();
      }
    }, /*#__PURE__*/React.createElement(CloneIconStyled, {
      style: {
        marginRight: "10px"
      }
    }), " ", i18n.t(tokens.labels.planDesign.cloneDesign)), isMasterSite && /*#__PURE__*/React.createElement(MenuItem, {
      onClick: () => {
        this.setAnchorEl('anchorElUpload', null);
        this.setState({
          uploadPlanDesignModalOpen: true
        });
      }
    }, /*#__PURE__*/React.createElement(UploadIconStyled, {
      style: {
        marginRight: "10px"
      }
    }), " ", i18n.t(tokens.labels.planDesign.uploadDesign)))), isMasterSite && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      transparent: true,
      customStyle: {
        height: 50,
        width: 150,
        textTransform: "capitalize",
        fontSize: 14,
        color: whitelabel.primaryColor || colors.skyBlue
      },
      "aria-controls": this.state.anchorElDownload ? 'demo-positioned-menu' : undefined,
      "aria-haspopup": "true",
      "aria-expanded": this.state.anchorElDownload ? 'true' : undefined,
      onClick: e => this.setAnchorEl('anchorElDownload', e.currentTarget)
    }, /*#__PURE__*/React.createElement(DownloadIconStyled, {
      style: {
        marginRight: 5
      }
    }), " Download Design"), /*#__PURE__*/React.createElement(Menu, {
      id: "demo-positioned-menu",
      "aria-labelledby": "demo-positioned-button",
      anchorEl: this.state.anchorElDownload,
      open: Boolean(this.state.anchorElDownload),
      onClose: () => this.setAnchorEl('anchorElDownload', null),
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      }
    }, /*#__PURE__*/React.createElement(MenuItem, {
      onClick: () => {
        this.setAnchorEl('anchorElDownload', null);
        this._downloadPlanDesignJson();
      }
    }, /*#__PURE__*/React.createElement(DownloadIconStyled, {
      style: {
        marginRight: 5
      }
    }), " Download JSON Design (Uploadable)"), /*#__PURE__*/React.createElement(MenuItem, {
      onClick: () => {
        this.setAnchorEl('anchorElDownload', null);
        this._downloadPlanDesignCsv();
      }
    }, /*#__PURE__*/React.createElement(DownloadIconStyled, {
      style: {
        marginRight: 5
      }
    }), " Download CSV Design")))), !this.props.isPrivateLabelAdvisor && designs[selectedClassID] && Object.keys(designs[selectedClassID]).length > 0 && /*#__PURE__*/React.createElement(Button, {
      color: "white",
      customStyle: {
        height: 50,
        width: 180,
        textTransform: "capitalize",
        fontSize: 16,
        marginRight: 1.25
      },
      onClick: this._toggleRevisions,
      noHover: true
    }, i18n.t(tokens.labels.planDesign.viewRevisions)), (isMasterSite || isPrivateLabel || this.props.currentAccountMeta.state <= 3) && this.state.selectedDesignID && /*#__PURE__*/React.createElement(Button, {
      customStyle: {
        height: 50,
        fontSize: 16,
        textTransform: "capitalize"
      },
      onClick: () => {
        // if we are saving a normal plan design from the master site then ask for an effective date
        if ((isMasterSite || isPrivateLabel) && !this.props.isQuote && !this.props.isRevision) {
          this._toggleEffectiveDateDialog();
        } else {
          this._saveDesign(false);
        }
      },
      color: "primary"
    }, i18n.t(tokens.labels.planDesign.saveDesign))), selectedClass.description ? /*#__PURE__*/React.createElement(Tooltip, {
      tip: selectedClass.description,
      placement: "top",
      arrow: true,
      display: "inline-block"
    }, /*#__PURE__*/React.createElement(Bubble, {
      label: `${i18n.t(tokens.titles.class)}: ${selectedClass.title}`,
      isSelected: true,
      clickable: false
    })) : /*#__PURE__*/React.createElement(Bubble, {
      label: `${i18n.t(tokens.titles.class)}: ${selectedClass.title}`,
      isSelected: true,
      clickable: false
    }), !isMasterSite && !isPrivateLabel && this.props.currentAccountMeta.state > 3 && /*#__PURE__*/React.createElement(Text, {
      color: "red"
    }, i18n.t(tokens.labels.planDesign.planDesignsCannotBeChanged)), !designs[selectedClassID] || Object.keys(designs[selectedClassID]).length === 0 ? /*#__PURE__*/React.createElement(Text, {
      medium: true
    }, i18n.t(tokens.labels.planDesign.noPlanDesignsForClass)) : /*#__PURE__*/React.createElement(React.Fragment, null, !this.props.isPrivateLabelAdvisor && /*#__PURE__*/React.createElement(Tabs, {
      value: this.state.value,
      onChange: this._handleTabChange,
      indicatorColor: "primary",
      textColor: "primary"
    }, orderedDesigns.map((designID, i) => {
      const design = designs[selectedClassID][designID];
      return /*#__PURE__*/React.createElement(Tab, {
        key: `${design.title}-${i}`,
        label: /*#__PURE__*/React.createElement(Text, {
          small: true,
          color: "primary"
        }, i18n.locale === "fr" ? design.title.replace("Design", "Conception") : design.title),
        sx: {
          width: 160,
          padding: '6px 12px'
        }
      });
    })), this.state.selectedDesignID && /*#__PURE__*/React.createElement(PlanDesignForm, {
      selectedDesignID: this.state.selectedDesignID,
      selectedClassID: selectedClassID,
      designs: designs,
      specialityProductMetaConfigs: this.props.specialityProductMetaConfigs,
      errors: this.state.errors,
      isMasterSite: isMasterSite,
      savePlanDesignBenefitInitData: this.props.savePlanDesignBenefitInitData,
      savePlanDesignField: this.props.savePlanDesignField,
      whitelabel: whitelabel,
      setChangesMadeFlag: this._setChangesMadeFlag,
      accountMeta: this.props.currentAccountMeta,
      isPrivateLabelAdvisor: this.props.isPrivateLabelAdvisor,
      language: this.props.language
    })), (isMasterSite || this.props.currentAccountMeta.state <= 3) && this.state.selectedDesignID && !this.props.overrideModularView && /*#__PURE__*/React.createElement(Button, {
      customStyle: {
        display: 'block',
        height: 50,
        fontSize: 16,
        textTransform: "capitalize",
        marginLeft: 'auto',
        marginTop: 3
      },
      onClick: () => {
        // if we are saving a normal plan design from the master site then ask for an effective date
        if (isMasterSite && !this.props.isQuote && !this.props.isRevision) {
          this._toggleEffectiveDateDialog();
        } else {
          this._saveDesign(false);
        }
      },
      color: "primary"
    }, i18n.t(tokens.labels.planDesign.saveDesign))), /*#__PURE__*/React.createElement(Modal, {
      open: this.state.showSaveMessageDialog,
      onClose: async () => {
        if (this.state.dialogCallback) {
          await this.state.dialogCallback();
        }
        this.setState({
          showSaveMessageDialog: false,
          dialogCallback: null,
          unSavedChangesMade: false
        });
      },
      title: i18n.t(tokens.labels.planDesign.changesMade),
      primaryButton: {
        label: "Save",
        onClick: async () => {
          await this._saveDesign(false);
          if (this.state.dialogCallback) {
            await this.state.dialogCallback();
          }
          this.setState({
            showSaveMessageDialog: false,
            dialogCallback: null
          });
        }
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      light: true
    }, i18n.t(tokens.labels.planDesign.unsavedChanges))), /*#__PURE__*/React.createElement(Modal, {
      open: this.state.showEffectiveDateDialog,
      onClose: this._toggleEffectiveDateDialog,
      title: i18n.t(tokens.titles.setEffectiveDate),
      primaryButton: {
        label: "Submit",
        onClick: () => this._saveDesign(false)
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement(Text, {
      medium: true
    }, i18n.t(tokens.labels.pleaseProvideEffectiveDate)), /*#__PURE__*/React.createElement(DateInput, {
      label: "Effective Date",
      value: this.state.effectiveDate,
      onChange: e => {
        this.setState({
          effectiveDate: e.target.value
        });
      },
      error: Boolean(this.state.effectiveDateError),
      helperText: this.state.effectiveDateError,
      fullWidth: true,
      required: true
    }), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "10px 0 0 0"
    }, i18n.t(tokens.labels.changesAppliedOnEffectiveDate))), /*#__PURE__*/React.createElement(FileDropZoneModal, {
      title: "Please upload the plan design file",
      hideModal: () => {
        this.setState({
          uploadPlanDesignModalOpen: false
        });
      },
      showModal: this.state.uploadPlanDesignModalOpen,
      maxItems: 1,
      type: "Plan Design Upload Form",
      submitFile: this._uploadPlanDesign,
      acceptedFiles: "text/json, .json",
      loader: this.props.loader
    }), this.props.loader?.isLoading && this.props.loader.type === 'container' && /*#__PURE__*/React.createElement(Loader, {
      isContainer: true
    }));
  }
}
export default PlanDesign;